import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import teamService from '@/services/teamService';
import sessionService from '@/services/sessionService';
import type { TeamResponse } from '@/services/teamService';
import type { User } from '@/types/auth';
import type { TeamStatsResponse, SalespersonStats } from '@/types/session';

interface UseTeamManagementReturn {
  teams: TeamResponse[];
  salespeople: User[];
  unassigned: User[];
  teamStats: TeamStatsResponse | null;
  statsByUserId: Record<string, SalespersonStats>;
  loading: boolean;
  error: string;
  refresh: () => void;
  addMember: (teamId: string, userId: string) => Promise<void>;
  removeMember: (teamId: string, userId: string) => Promise<void>;
  transferMember: (fromTeamId: string, userId: string, toTeamId: string) => Promise<void>;
  transferAllMembers: (fromTeamId: string, toTeamId: string) => Promise<void>;
}

export function useTeamManagement(): UseTeamManagementReturn {
  const { t } = useTranslation();
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [salespeople, setSalespeople] = useState<User[]>([]);
  const [teamStats, setTeamStats] = useState<TeamStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      setError('');
      const [teamsResult, spResult, statsResult] = await Promise.allSettled([
        teamService.getMyTeams(),
        teamService.getSalespeople(),
        sessionService.getTeamStats(),
      ]);
      setTeams(teamsResult.status === 'fulfilled' ? teamsResult.value : []);
      setSalespeople(spResult.status === 'fulfilled' ? spResult.value : []);
      setTeamStats(statsResult.status === 'fulfilled' ? statsResult.value : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('teamManagement.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const addMember = useCallback(async (teamId: string, userId: string) => {
    try {
      await teamService.addMember(teamId, userId);
      await fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  }, [fetchAll]);

  const removeMember = useCallback(async (teamId: string, userId: string) => {
    try {
      await teamService.removeMember(teamId, userId);
      await fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  }, [fetchAll]);

  const transferMember = useCallback(async (fromTeamId: string, userId: string, toTeamId: string) => {
    try {
      await teamService.removeMember(fromTeamId, userId);
      await teamService.addMember(toTeamId, userId);
      await fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  }, [fetchAll]);

  const unassigned = salespeople.filter((sp) => !sp.teamId);

  const statsByUserId = useMemo(() => {
    const map: Record<string, SalespersonStats> = {};
    (teamStats?.salespersonStats ?? []).forEach((s) => {
      map[s.userId] = s;
    });
    return map;
  }, [teamStats?.salespersonStats]);

  const transferAllMembers = useCallback(async (fromTeamId: string, toTeamId: string) => {
    const team = teams.find((t) => t.id === fromTeamId);
    if (!team) return;
    for (const member of team.members) {
      await teamService.removeMember(fromTeamId, member.id);
      await teamService.addMember(toTeamId, member.id);
    }
    await fetchAll();
  }, [teams, fetchAll]);

  return { teams, salespeople, unassigned, teamStats, statsByUserId, loading, error, refresh: fetchAll, addMember, removeMember, transferMember, transferAllMembers };
}
